import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { useOrganizationGeozonesQuery } from "../../../api/network/adminApiServices";
import { useLazyGImageryMetaQuery } from "../../../api/network/mapApiServices";
import {
  setMapStateData,
  setModalsData,
  TModalsState,
} from "../../../api/store/commonSlice";
import {
  CameraListingResponse,
  DeviceListingResponseWAddress,
} from "../../../api/types/Camera";
import {
  LegacyGeozoneEntityListingPayload,
  LegacyGeozoneType,
  TLegacyGeozone,
} from "../../../api/types/Geozone";
import { LegacyVerifyEmailOtpResponseSuccess } from "../../../api/types/Onboarding";
import BasicMap, {
  MapLoadingAnimation,
} from "../../../components/maps/basicMap";
import MapTopRightControls from "../../../components/maps/mapTopRightControls";
import {
  APP_CONFIG,
  GOOGLE_MAP_MODE_NAMES,
} from "../../../constants/constants";
import { useGMapMode } from "../../../hooks/mapGoogle";
import { useMapInit } from "../../../hooks/mapInit";
import {
  TGeozoneMapDataCircle,
  TGeozoneMapDataPolygon,
  TLatLng,
  TMapLayerOptions,
  TMapState,
  TMapType,
} from "../../../types/map";
import { hexToRgb } from "../../../utils/common";
import {
  filterOutWhiteGeozones,
  getGeozoneShapeCenter,
} from "../../../utils/geozone";
import { useLoggedInUserData } from "../../../utils/user";
import { mapOperations as mapOperationsCircle } from "../../admin-details/geozone/map-circle";
import { mapOperations as mapOperationsPolygon } from "../../admin-details/geozone/map-polygon";
import { mapOperations as mapOperationsRoute } from "../../admin-details/geozone/map-route";
import { TMapOperationsProps } from "../../admin-details/geozone/type";
import {
  geozonePrepareMapState,
  getGeozoneLocs,
} from "../../admin-details/geozone/util";
import ModalLayerFilters from "../../map-fleet/modalLayerFilters";
import { TMapRefGeozones } from "../../map-fleet/type";
import { mapOperations } from "./map";
import { TMapData, TMapRef, TMemoizedMapOperationsProps } from "./type";
import DirectionsPanel from "../../../components/maps/DirectionPanel";
import ModalClusterVehiclesList from "../../map-fleet/modalClusterVehiclesList";
import {
  CLUSTER_VEHICLES_VIEW_MORE_EVENT,
  ClusterVehiclesListItem,
  ClusterVehiclesViewMoreEventDetail,
} from "../../../utils/map";

type CameraListingWidgetMapProps = {
  dataDeviceList:
    | CameraListingResponse<DeviceListingResponseWAddress[]>
    | undefined;
  isFetchingDeviceList: boolean;
  isMapCollapsed?: boolean;
};

const CameraListingWidgetMap: React.FC<CameraListingWidgetMapProps> = ({
  dataDeviceList,
  isFetchingDeviceList,
  isMapCollapsed = false,
}) => {
  if (APP_CONFIG.DEBUG.MAPS) console.log(">>>>", dataDeviceList?.data, ">>");
  const { t: tMain } = useTranslation();

  const loggedInUserData = useSelector(
    (state: { commonReducer: { user: LegacyVerifyEmailOtpResponseSuccess } }) =>
      state.commonReducer.user
  );
  const modalsState: TModalsState = useSelector(
    (state: any) => state.commonReducer.modals
  );
  const mapState = useSelector(
    (state: { commonReducer: { mapState: TMapState } }) =>
      state.commonReducer.mapState
  );
  const dispatch = useDispatch();

  const { initMap, gIdentifyAndSetMapModeToStore } = useMapInit();
  const { gMapGetMode, gMapSetMode } = useGMapMode();

  const mapWrapperRef = React.createRef<HTMLDivElement>();

  const clearSearchRef = useRef<((preserveZoom?: boolean) => void) | null>(
    null
  );

  const [clusterVehiclesModal, setClusterVehiclesModal] = useState<{
    visible: boolean;
    count: number;
    entityLabel: string;
    markers: ClusterVehiclesListItem[];
  }>({
    visible: false,
    count: 0,
    entityLabel: "assets",
    markers: [],
  });

  const closeClusterVehiclesModal = useCallback(() => {
    setClusterVehiclesModal((prev) => ({ ...prev, visible: false }));
  }, []);

  const normalizedCameraDataPoints = React.useMemo(
    () =>
      dataDeviceList?.data?.map((camPoint) => ({
        ...camPoint,
        // use fleet device id to keep cluster marker/title lookup consistent
        device_id: camPoint.ft_device_id,
      })) || [],
    [dataDeviceList?.data]
  );

  useEffect(() => {
    const handleClusterViewMore = (event: Event) => {
      const customEvent =
        event as CustomEvent<ClusterVehiclesViewMoreEventDetail>;
      if (!customEvent?.detail) return;

      setClusterVehiclesModal({
        visible: true,
        count: customEvent.detail.count,
        entityLabel: customEvent.detail.entityLabel,
        markers: customEvent.detail.markers || [],
      });
    };

    window.addEventListener(
      CLUSTER_VEHICLES_VIEW_MORE_EVENT,
      handleClusterViewMore as EventListener
    );

    return () => {
      window.removeEventListener(
        CLUSTER_VEHICLES_VIEW_MORE_EVENT,
        handleClusterViewMore as EventListener
      );
    };
  }, []);

  // fetch geozones data
  const thisUserOrganizationId = useLoggedInUserData("ownerOrganizationId");
  const thisOrgAssetImageOnMap = useLoggedInUserData("assetimageonmap");
  const isAssetImageEnabled = thisOrgAssetImageOnMap === "1";
  const { data: dataOrgGeozones, isFetching: isFetchingOrgGeozones } =
    useOrganizationGeozonesQuery(
      {
        accountid: `${thisUserOrganizationId}`,
        page_size: APP_CONFIG.LISTINGS.XL_PAGE_SIZE,
        page: 1,
      } as LegacyGeozoneEntityListingPayload,
      {
        skip: !mapState?.mapScriptLoaded,
      } // || mapState?.mapLayerOptions?.hide_geozones
    );

  // common map ref for geozone map operations
  const mapRefGeozones = React.useRef<TMapRefGeozones>({});
  const [directionsRenderer, setDirectionsRenderer] =
    React.useState<google.maps.DirectionsRenderer | null>(null);

  const handleDirectionsChange = (
    renderer: google.maps.DirectionsRenderer | null
  ) => {
    setDirectionsRenderer(renderer);
  };

  // used for any map state transition to refresh map entities
  const [mapStateTransitionInProgress, setMapStateTransitionInProgress] =
    React.useState(false);

  const [gImageryMetaApiTrigger, { isFetching: isFetchingGImageryMeta }] =
    useLazyGImageryMetaQuery();

  // common map ref to be used for various map operations
  const mapRef = React.useRef<TMapRef>({
    map: null,
    objects: {
      mPushpins: [],
      mClusterLayer: null,
    },
  });

  const loadResetMapDataWithInitialValues = () => {
    if (APP_CONFIG.DEBUG.MAPS)
      console.log("[loadResetMapDataWithInitialValues]");
    setMapStateTransitionInProgress(true);
    const newMapState: Partial<TMapState> = {
      // ...mapState,
      mapCenter: APP_CONFIG.MAPS.DEFAULTS.CENTER,
      mapData: {
        centerPosition: APP_CONFIG.MAPS.DEFAULTS.CENTER,
        ready: true,
      },
    };
    if (APP_CONFIG.DEBUG.MAPS) console.log("newMapState", newMapState);
    setTimeout(() => {
      dispatch(setMapStateData(newMapState));
      setMapStateTransitionInProgress(false);
    }, 200);
  };

  // set map data on page load
  React.useEffect(() => {
    if (
      !!dataDeviceList &&
      !isFetchingDeviceList &&
      !!dataOrgGeozones &&
      !isFetchingOrgGeozones
    ) {
      dispatch(setModalsData({ ...modalsState, showVehicleFilter: false }));
      // setDataPoints(allRetrievedVehicles);
      // setCheckedVehicles(
      //   allRetrievedVehicles?.map((item) => getVehicleUUID(item)) ?? []
      // );
      loadResetMapDataWithInitialValues();
    }
  }, [
    dataDeviceList,
    isFetchingDeviceList,
    dataOrgGeozones,
    isFetchingOrgGeozones,
  ]);

  const renderGeozones = React.useCallback(() => {
    if (mapState?.mapLayerOptions?.hide_geozones) return;
    if (
      !!dataOrgGeozones &&
      !isFetchingOrgGeozones
      // &&
      // mapState?.mapData?.ready
    ) {
      if (APP_CONFIG.DEBUG.MAPS)
        console.log("[handleMapReady] [renderGeozones]");
      dataOrgGeozones?.results
        ?.filter(filterOutWhiteGeozones)
        ?.forEach((geozone) => {
          // initialize map ref for each geozone
          const thisGeozoneMapRef = (mapRefGeozones.current[geozone.geozoneid] =
            {
              current: {
                map: mapRef.current.map,
                objects: {},
              },
            });
          // prepare map state for each geozone
          const geozoneLocs = getGeozoneLocs(
            geozone as Partial<TLegacyGeozone>
          );
          const geozoneCenter = getGeozoneShapeCenter(
            geozone as Partial<TLegacyGeozone>,
            "object"
          ) as TLatLng;
          const mapDataFromSavedRecord = {
            //values.properties as TGeozoneMapData;
            centerPosition: geozoneCenter,
            locs: geozoneLocs,
            radius: geozone.radius,
          };
          const newMapState: TMapState = geozonePrepareMapState({
            seedMapState: mapState,
            shapeType: geozone.zonetype,
            seedMapData: mapDataFromSavedRecord,
            centerPosition:
              mapDataFromSavedRecord?.centerPosition ??
              APP_CONFIG.MAPS.DEFAULTS.CENTER,
            polygonSides:
              (mapDataFromSavedRecord as TGeozoneMapDataPolygon)?.locs
                ?.length ?? APP_CONFIG.MAPS.DEFAULTS.POLYGON_POINTS,
            locs:
              (mapDataFromSavedRecord as TGeozoneMapDataPolygon)?.locs ??
              undefined,
            radius:
              (mapDataFromSavedRecord as TGeozoneMapDataCircle)?.radius ??
              APP_CONFIG.MAPS.DEFAULTS.RADIUS,
            color: hexToRgb(`${geozone.shapecolor}`), // APP_CONFIG.MAPS.COLORS[values.shapecolor as keyof typeof APP_CONFIG.MAPS.COLORS],
            editable: false,
            ready: true,
          });
          const mapOperationsParams = {
            mapRef: thisGeozoneMapRef, // mapRefGeozones.current[geozone.geozoneid],
            mapData: newMapState.mapData || mapDataFromSavedRecord,
            setMapData: () => {},
            drawShapeOnly: true,
            geozoneId:
              loggedInUserData?.user?.enablegeozonelabels === 1
                ? geozone.geozoneid
                : undefined,
          } as TMapOperationsProps;
          // check zone type and call respective map operations
          if (geozone.zonetype === null || isNaN(geozone.zonetype)) return; // prevents error when zonetype is null due to bad API data
          switch (parseInt(geozone?.zonetype?.toString())) {
            case LegacyGeozoneType.CIRCLE:
              if (APP_CONFIG.DEBUG.GEOZONES)
                console.log("geozoneid:", geozone.geozoneid, "[CIRCLE]");
              mapOperationsCircle(mapOperationsParams);
              break;
            case LegacyGeozoneType.POLYGON:
              if (APP_CONFIG.DEBUG.GEOZONES)
                console.log("geozoneid:", geozone.geozoneid, "[POLYGON]");
              mapOperationsPolygon(mapOperationsParams);
              break;
            case LegacyGeozoneType.ROUTE:
              if (APP_CONFIG.DEBUG.GEOZONES)
                console.log("geozoneid:", geozone.geozoneid, "[ROUTE]");
              mapOperationsRoute(mapOperationsParams);
              break;
            default:
              break;
          }
        });
    } else {
      if (APP_CONFIG.DEBUG.MAPS)
        console.log(
          "[handleMapReady] [renderGeozones] ELSE ",
          !!dataOrgGeozones,
          !isFetchingOrgGeozones,
          !!mapState?.mapData?.ready
        );
    }
  }, [
    dataOrgGeozones,
    isFetchingOrgGeozones,
    mapState?.mapLayerOptions?.hide_geozones,
    mapState?.mapData?.ready,
  ]);

  // implemented to revert back to last zoomed view when vehicle details modal is closed
  // however, this behaviour is disabled as of 7/2/2024 as it is clashing with other map operations
  const [lastMapView, setLastMapView] = React.useState<any>(null);

  // helper function to get map operations props
  const getMapOpsProps: TMemoizedMapOperationsProps = React.useCallback(() => {
    return {
      mapRef,
      mapState,
      mapData: { ...mapState?.mapData } as TMapData,
      mapLayerOptions: mapState?.mapLayerOptions as TMapLayerOptions,
      setMapData: () => {},
      dataPoints: dataDeviceList?.data || [],
      isAssetImageEnabled,
      onViewChangeEnd: (center, zoom) => {
        if (APP_CONFIG.DEBUG.MAPS)
          console.log("[onViewChangeEnd] center:", center, "zoom:", zoom);
        dispatch(
          setMapStateData({
            // ...mapState,
            mapCenter: center,
            mapZoom: zoom,
            mapData: {
              ...mapState.mapData,
              centerPosition: center,
            },
            mapType:
              gMapGetMode(mapRef.current.map) ===
              GOOGLE_MAP_MODE_NAMES.STREETVIEW
                ? (APP_CONFIG.MAPS.TYPES[APP_CONFIG.MAPS.TYPES_INDEX.ROADMAP]
                    .type as TMapType)
                : mapRef.current.map?.getMapTypeId() ===
                    APP_CONFIG.MAPS.TYPES[APP_CONFIG.MAPS.TYPES_INDEX.SATELLITE]
                      .id
                  ? (APP_CONFIG.MAPS.TYPES[
                      APP_CONFIG.MAPS.TYPES_INDEX.SATELLITE
                    ].type as TMapType)
                  : mapState.mapType,
          })
        );
        // this function gets triggered on closing streetside view
        // make map entities visible here
        if (
          mapState?.mapType ===
            APP_CONFIG.MAPS.TYPES[APP_CONFIG.MAPS.TYPES_INDEX.ROADMAP].type &&
          mapRef.current.map?.getMapTypeId() ===
            APP_CONFIG.MAPS.TYPES[APP_CONFIG.MAPS.TYPES_INDEX.ROADMAP].id
        ) {
          if (APP_CONFIG.DEBUG.MAPS)
            console.log(
              `Attempt to switch to road view (when closing streetside), params: ${mapState?.mapType}`
            );
        }
        // if(APP_CONFIG.DEBUG.MAPS) console.log('Switching to road view after streetside view is closed')
        // handleMapTypeChange('road')
      },
      onMapTypeChange: (event) => {
        if (APP_CONFIG.DEBUG.MAPS) console.log("[onMapTypeChange]", event);
        // with google maps, store value updates automatically via `tilesloaded` event, so
        // we just update the map mode using Google Maps JS SDK using `gMapSetMode` in
        // `handleMapTypeChange` below
        // -------------------------------------------------------------------------------
        // dispatch(
        //   setMapStateData({
        //     ...mapState,
        //     mapType:
        //       (APP_CONFIG.MAPS.TYPES.find(
        //         (item) => item.id === event.newMapTypeId
        //       )?.type as TMapType) ?? "",
        //   })
        // );
      },
      saveLastView: (center: any, zoom: number) => {
        if (APP_CONFIG.DEBUG.MAPS) console.log("[saveLastView]", center, zoom);
        setLastMapView({ center, zoom });
      },
    };
  }, [mapState, dataDeviceList?.data]);

  // carry out map operations on map ready
  const handleMapReady = React.useCallback(() => {
    if (APP_CONFIG.DEBUG.MAPS) console.log("[handleMapReady]");
    mapOperations({
      ...getMapOpsProps(),
      dataPoints: normalizedCameraDataPoints,
      onDataPointPushpinClick: (dataPoint: DeviceListingResponseWAddress) => {
        if (clearSearchRef.current) {
          clearSearchRef.current(true);
        }
        if (APP_CONFIG.DEBUG.MAPS) console.log("CLICKED", dataPoint);
        // selectMapVehicle(
        //   selectedVehicle === getVehicleUUID(dataPoint)
        //     ? null
        //     : getVehicleUUID(dataPoint)
        // );
      },
    });
    renderGeozones();
  }, [
    normalizedCameraDataPoints,
    dataOrgGeozones,
    isFetchingOrgGeozones,
    mapState?.mapLayerOptions?.clustering,
    mapState?.mapLayerOptions?.hide_geozones,
    mapState?.mapData?.ready,
  ]);

  const handleMapTypeChange = (
    mapType: TMapType,
    latLng?: google.maps.LatLng
  ) => {
    if (APP_CONFIG.DEBUG.MAPS) console.log("[handleMapTypeChange]", mapType);

    const successCallback = () => {
      gMapSetMode(
        mapRef.current.map,
        mapType as TMapType,
        latLng || mapRef.current.map.getCenter(),
        {
          // callback checks if streetside mode is enabled and sets applicable map type in store
          onStreetViewVisibleChanged: (panoVisible: boolean) => {
            if (APP_CONFIG.DEBUG.MAPS)
              console.log(
                "[onStreetViewVisibleChanged] attached at map-heat.tsx"
              );
            const newMapMode = gIdentifyAndSetMapModeToStore(
              mapRef.current.map
            );
            if (APP_CONFIG.DEBUG.MAPS)
              console.log("SVCHANGED newMapMode:", newMapMode);
            const panorama = mapRef.current.map.getStreetView();
            setTimeout(() => {
              if (!panorama.getVisible()) {
                // exited streetview
                const newMapMode = gIdentifyAndSetMapModeToStore(
                  mapRef.current.map
                );
                if (APP_CONFIG.DEBUG.MAPS)
                  console.log(
                    "SVCHANGED - pano not visible (exited) newMapMode:",
                    newMapMode
                  );
                // update user map activity in store
                // if (!recordActivityAsPage || refreshingRef.current) return;
                // if (userMapRefreshing) {
                //   if (APP_CONFIG.DEBUG.MAPS)
                //     console.log(
                //       "SVCHANGED - NO - userMapRefreshing",
                //       userMapRefreshing,
                //       "RETURNING"
                //     );
                //   return;
                // }
                if (APP_CONFIG.DEBUG.MAPS)
                  console.log(
                    "SVCHANGED - about to trigger usermapactivity change",
                    newMapMode
                  );
                // check if streetview exit was not successful, trigger change to roadmap
                if (newMapMode === "streetview") {
                  if (APP_CONFIG.DEBUG.MAPS)
                    console.log(
                      "SVCHANGED - fix incorrect 'newMapMode' value from 'streetview' to ",
                      GOOGLE_MAP_MODE_NAMES.ROADMAP
                    );
                  gMapSetMode(
                    mapRef.current.map,
                    GOOGLE_MAP_MODE_NAMES.ROADMAP,
                    latLng || mapRef.current.map.getCenter()
                  );
                }
                setTimeout(() => {
                  const newMapMode = gIdentifyAndSetMapModeToStore(
                    mapRef.current.map
                  );
                  if (APP_CONFIG.DEBUG.MAPS)
                    console.log(
                      "SVCHANGED - about to trigger setUserMapActivity change - newMapMode:",
                      newMapMode
                    );
                  // dispatch(
                  //   setUserMapActivity({
                  //     ...userMapActivity,
                  //     assetMap: {
                  //       // ...activityRef.current?.fleetMap,
                  //       ...userMapActivity?.assetMap,
                  //       center: {
                  //         latitude: mapRef.current.map.getCenter().lat(),
                  //         longitude: mapRef.current.map.getCenter().lng(),
                  //       } as TLatLng,
                  //       zoom: mapRef.current.map.getZoom(),
                  //       mapType: newMapMode,
                  //     },
                  //   } as TUserMapActivity)
                  // );
                }, 600);
              } else {
                if (APP_CONFIG.DEBUG.MAPS)
                  console.log("SVCHANGED - pano is visible");
              }
            }, 600);
          },
        }
      );
    };
    // Handle street view
    if (APP_CONFIG.MAPS.VIEW_TYPES_REQUIRING_METADATA_CHECK.includes(mapType)) {
      const center: google.maps.LatLng = mapRef.current.map.getCenter();
      if (
        typeof center.lat() !== "number" ||
        typeof center.lng() !== "number"
      ) {
        if (APP_CONFIG.DEBUG.MAPS) console.error("Map center coords not found");
        toast.error(tMain("maps.type_not_supported"));
        return;
      }
      gImageryMetaApiTrigger({ lat: center.lat(), lng: center.lng() })
        .unwrap()
        .then((response) => {
          if (APP_CONFIG.DEBUG.MAPS)
            console.log("imageryMetaApiTrigger response", response);
          if ((response?.status as any) === "OK") {
            successCallback();
          } else {
            toast.error(tMain("maps.type_not_supported"));
          }
        })
        .catch((error) => {
          toast.error(tMain("maps.type_not_supported"));
        });
    } else {
      successCallback();
    }
  };

  const handleMapLayerChange = (mapLayerOptions: TMapLayerOptions) => {
    setMapStateTransitionInProgress(true);
    setTimeout(() => {
      dispatch(
        setMapStateData({
          // ...mapState,
          mapLayerOptions,
        })
      );
      setMapStateTransitionInProgress(false);
    }, 100);
  };

  const handleModalCloseClick = () => {
    // Reset map to its default state
    // mapUpdatesHandler(getMapOpsProps(), "checkedUpdated", checkedVehicles);
  };

  // hide streetview when user clicks anywhere outside map
  useEffect(() => {
    const handleClickOutside = (event: any) => {
      if (
        mapState.mapType === GOOGLE_MAP_MODE_NAMES.STREETVIEW &&
        mapWrapperRef.current &&
        !mapWrapperRef.current.contains(event.target)
      ) {
        gMapSetMode(
          mapRef.current.map,
          mapRef.current.map?.lastMapModeBeforeStreetview ||
            GOOGLE_MAP_MODE_NAMES.ROADMAP,
          mapRef.current.map.getCenter()
          // { skipEventListenerRegistration: true }
        );
      }
    };

    if (mapState?.mapType === GOOGLE_MAP_MODE_NAMES.STREETVIEW) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [mapState?.mapType, mapWrapperRef, mapRef]);

  if (
    mapStateTransitionInProgress ||
    isFetchingOrgGeozones ||
    isFetchingDeviceList ||
    !mapState?.mapData?.ready
  ) {
    return (
      <div className="h-full w-full flex items-center justify-center relative">
        <MapLoadingAnimation
          // bgClassName="bg-red-200"
          bgOpacityClassName="bg-opacity-100"
        />
      </div>
    );
  }

  return (
    <div className="h-96" ref={mapWrapperRef}>
      <BasicMap
        className="bg-gray-200 h-full w-full"
        mapRef={mapRef}
        mapData={mapState?.mapData}
        // setMapData={() => {}}
        onMapReady={handleMapReady}
      />
      <MapTopRightControls
        mapRef={mapRef as React.RefObject<{ map: any }>}
        onDirectionChange={handleDirectionsChange}
        onGAutocompleteClearCallback={() => {
          // reset map to default view
          // mapUpdatesHandler(
          //   getMapOpsProps(),
          //   "centerToPushpin",
          //   checkedVehicles
          // );
        }}
        clearSearchRef={clearSearchRef}
      />
      <DirectionsPanel
        directionsRenderer={directionsRenderer}
        onClose={() => setDirectionsRenderer(null)}
        isMapCollapsed={isMapCollapsed}
      />
      <ModalClusterVehiclesList
        visible={clusterVehiclesModal.visible}
        count={clusterVehiclesModal.count}
        entityLabel={clusterVehiclesModal.entityLabel}
        markers={clusterVehiclesModal.markers}
        showCameraId={true}
        showViewDetails={false}
        onHide={closeClusterVehiclesModal}
      />
      <ModalLayerFilters
        onMapTypeChange={handleMapTypeChange}
        onMapLayerChange={handleMapLayerChange}
        onMapCloseClick={handleModalCloseClick}
      />
    </div>
  );
};
export default CameraListingWidgetMap;
